import {
  AlertDangerIcon,
  Button,
  Caption,
  css,
  Inline,
  Mode,
  Paragraph,
  styled,
  useModeContext,
} from 'canoe';
import React, { useCallback, useMemo, useState } from 'react';
import { DropzoneInputProps, DropzoneRootProps, useDropzone } from 'react-dropzone';
import { zIndex } from 'styleguide';

import { ValueBox, ValueGroup, ValueToggle } from '@candu-ai/craft/ui';
import Theme from 'canoe/types/theme';
import { useToggle } from 'commons/hooks';
import { Image as ImageIcon } from 'craft/icons';

import { ImageLinkForm } from './ImageLinkForm';
import { MediaLibrary } from './MediaLibrary';
import { Loading, uploadImage } from './uploadImage';

const states = {
  LINK: 'link',
  UPLOAD: 'upload',
};

const droppableOutline = ({
  theme,
  accepted,
  rejected,
  mode,
}: {
  theme: Theme;
  accepted?: boolean;
  rejected?: boolean;
  mode?: Mode;
}) => css`
  border: 1px dashed ${theme.colors.OnyxGrayL30};

  ${
    mode === 'Dark' &&
    css`
      border-color: ${theme.colors.GlitterGrayD10};
    `
  }

  ${
    accepted &&
    css`
      border-color: ${theme.colors.Success};
    `
  }

  ${
    rejected &&
    css`
      border-color: ${theme.colors.Danger};
    `
  }
`;

export const PlaceholderWrapper = styled.div<
  { accepted?: boolean; rejected?: boolean; isPopover?: boolean; mode?: Mode } & DropzoneRootProps
>`
  ${({ theme, isPopover, accepted, rejected, mode }) => css`
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    flex: 1;
    width: 100%;
    height: 100%;
    border-radius: ${theme.borderRadius.small};
    position: relative;
    overflow: hidden;

    ${!isPopover &&
    css`
      padding: 2em;
      ${droppableOutline({ theme, accepted, rejected, mode })};
    `}
  `}
`;

export const DroppableWrapper = styled(PlaceholderWrapper)`
  ${({ theme, accepted, rejected, mode }) => css`
    padding: 0 ${theme.spaces.s};
    ${droppableOutline({ theme, accepted, rejected, mode })};
  `}
`;

const ProgressBar = styled.div<{ progress: number }>`
  ${({ theme, progress }) => css`
    width: 100%;
    height: 8px;
    display: block;
    margin-top: ${theme.spaces.xs};
    background-color: ${theme.colors.White};
    border-radius: ${theme.borderRadius.small};
    position: relative;
    overflow: hidden;

    &:after {
      content: '';
      width: ${progress}%;
      height: 8px;
      background-color: ${theme.colors.AquaTeal};
      position: absolute;
      top: 0;
      left: 0;
      transition: width 1s;
    }
  `}
`;

export const DraggableInput = styled.input<
  { disabled?: boolean; isPopover?: boolean; [key: string]: any } & DropzoneInputProps
>`
  ${({ theme }) => css`
    width: 100%;
    height: calc(100% + 40px);
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    top: -40px;
    z-index: ${zIndex.lowest};
    outline: none;

    input[type='button'],
    input[type='button'] {
      background-color: ${theme.colors.Danger};
    }
  `}
`;

export const PlaceholderIcon = styled(ImageIcon)`
  ${({ theme }) => css`
    margin: ${theme.spaces.s} 0 ${theme.spaces.xs};
  `}
`;

export const PlaceholderParagraph = styled(Paragraph)<{ isTitle?: boolean; mode?: Mode }>`
  ${({ theme, isTitle, mode }) => css`
    max-width: 100%;
    color: ${theme.colors.OnyxGrayL30};
    white-space: normal;

    ${isTitle &&
    css`
      margin-bottom: ${theme.spaces.xxs};
      color: ${theme.colors.OnyxGrayL10};
      font-weight: ${theme.fontWeights.bold};
    `}

    ${mode === 'Dark' &&
    css`
      color: ${theme.colors.GlitterGrayL20};

      ${isTitle &&
      css`
        color: ${theme.colors.GlitterGrayL30};
      `}
    `}
  `}
`;

export const UploadError = styled(Caption)`
  ${({ theme }) => css`
    width: 250px;
    margin: ${theme.spaces.xs} 0 0;
    color: ${theme.colors.Danger};
    white-space: normal;
  `}
`;

export const PlaceholderCTAWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: ${theme.spaces.s} 0;
  `}
`;

interface MediaPlaceholderProps {
  onSelectImage: (url: string, dimensions?: { width: number; height: number }) => void;
  additionalSectionName?: string;
  additionalSectionComponent?: React.ReactNode;
  isPopover?: boolean;
}

export const MediaPlaceholder = ({
  onSelectImage,
  additionalSectionName,
  additionalSectionComponent,
  isPopover,
}: MediaPlaceholderProps) => {
  const [active, setActive] = useState(additionalSectionName || states.UPLOAD);

  const [isModalOpen, toggleModal] = useToggle(false);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState<Loading>({ status: false, name: '', error: undefined });

  const { mode } = useModeContext();

  const { getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
    accept: 'image/*',
    noClick: true,
    onDrop: (acceptedFiles: File[]) =>
      // @ts-ignore
      uploadImage(onSelectImage, setLoading, setProgress)(acceptedFiles),
  });

  const sourceTabs = useMemo(
    () => (
      <ValueGroup>
        <ValueBox responsiveToggle data-cy="image-placeholder" css={{ width: '308px' }} mode={mode}>
          <ValueToggle
            label="Link"
            toggleGroup="image-placeholder"
            variant="tabs"
            color="grey"
            appearance="borderless"
            disabled={active === states.LINK}
            onClick={() => setActive(states.LINK)}
            isActive={active === states.LINK}
          />
          <ValueToggle
            label="Upload"
            toggleGroup="image-placeholder"
            variant="tabs"
            color="grey"
            appearance="borderless"
            disabled={active === states.UPLOAD}
            onClick={() => setActive(states.UPLOAD)}
            isActive={active === states.UPLOAD}
          />
          {additionalSectionName && (
            <ValueToggle
              label={additionalSectionName}
              toggleGroup="image-placeholder"
              variant="tabs"
              color="grey"
              appearance="borderless"
              disabled={active === additionalSectionName}
              onClick={() => setActive(additionalSectionName)}
              isActive={active === additionalSectionName}
            />
          )}
        </ValueBox>
      </ValueGroup>
    ),
    [mode, active, states, additionalSectionName, setActive],
  );

  const getCopyWithStatus = useCallback(
    (description: string) => (
      <>
        <PlaceholderParagraph size="short" align="center" isTitle noMargin mode={mode}>
          {loading.status ? 'Uploading' : 'Upload'} image
        </PlaceholderParagraph>
        <PlaceholderParagraph size="short" align="center" noMargin={!loading.name} mode={mode}>
          {loading.name}
        </PlaceholderParagraph>
        <PlaceholderParagraph size="short" align="center" noMargin mode={mode}>
          {description}
        </PlaceholderParagraph>
        {!!loading.error && (
          <Inline space="xs" align="center">
            <AlertDangerIcon size="s" color="Danger" />
            <UploadError>{loading.error}</UploadError>
          </Inline>
        )}
      </>
    ),
    [loading],
  );

  const linkContent = useMemo(
    () => (
      <>
        {getCopyWithStatus('Add an image link here.')}
        <PlaceholderCTAWrapper>
          <ImageLinkForm onSelectImage={onSelectImage} />
        </PlaceholderCTAWrapper>
      </>
    ),
    [getCopyWithStatus, onSelectImage],
  );

  const uploadContent = useMemo(
    () => (
      <>
        {getCopyWithStatus('Drag an image into this area or click below to upload.')}
        <PlaceholderCTAWrapper>
          <Button variant="secondary" onClick={toggleModal}>
            Upload
          </Button>
          <MediaLibrary
            isModalOpen={isModalOpen}
            toggleModal={toggleModal}
            onSelectImage={onSelectImage}
          />
        </PlaceholderCTAWrapper>
      </>
    ),
    [getCopyWithStatus, toggleModal, isModalOpen, onSelectImage],
  );

  if (isPopover) {
    return (
      <PlaceholderWrapper isPopover mode={mode}>
        {sourceTabs}
        {active === states.UPLOAD ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <DroppableWrapper
              accepted={isDragAccept}
              rejected={isDragReject}
              {...getRootProps()}
              mode={mode}
            >
              <DraggableInput
                {...getInputProps()}
                type="file"
                disabled={loading.status}
                accept="image/*"
              />
              <PlaceholderIcon width={30} />
              {uploadContent}
            </DroppableWrapper>
            {!!loading.status && <ProgressBar progress={progress} />}
            <Button
              appearance="text"
              variant="contrast"
              size="xsmall"
              onClick={toggleModal}
              mt="xs"
            >
              Open Media Library
            </Button>
          </div>
        ) : (
          <>
            <PlaceholderIcon width={30} />
            {active === states.LINK && linkContent}
            {active === additionalSectionName && additionalSectionComponent}
            {!!loading.status && <ProgressBar progress={progress} />}
          </>
        )}
      </PlaceholderWrapper>
    );
  }

  return (
    <PlaceholderWrapper
      accepted={isDragAccept}
      rejected={isDragReject}
      {...getRootProps()}
      mode={mode}
    >
      <DraggableInput {...getInputProps()} type="file" disabled={loading.status} accept="image/*" />
      {sourceTabs}
      <PlaceholderIcon />
      {active === states.UPLOAD && uploadContent}
      {active === states.LINK && linkContent}
      {active === additionalSectionName && additionalSectionComponent}
      {!!loading.status && <ProgressBar progress={progress} />}
    </PlaceholderWrapper>
  );
};
